import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import WaiverPreview from '../components/waiver/WaiverPreview';
import SignaturePad from '../components/waiver/SignaturePad';
import PhoneInput from '../components/common/PhoneInput';
import { isValidEmail, ValidationMessages } from '../utils/validation';
import * as waiverService from '../services/waiverService';
import {
  WaiverTemplate,
  WaiverFormData,
  SignatureData,
  AddressInfo,
  EmergencyContactInfo,
  DEFAULT_CHECKBOX_TEXT,
} from '../types/waiver';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 100px 24px 24px 24px;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text};
  font-size: 28px;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  margin: 0;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const CardTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 18px;
  margin: 0 0 20px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${({ $fullWidth }) => ($fullWidth ? 'span 2' : 'span 1')};

  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.error};
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 16px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 8px;
  margin-top: 16px;

  input {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    cursor: pointer;
  }
`;

const CheckboxText = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  line-height: 1.5;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 14px 28px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? `
          background: ${theme.colors.primary};
          color: white;
          border: none;
          &:hover { opacity: 0.9; }
        `
      : `
          background: transparent;
          color: ${theme.colors.text};
          border: 1px solid ${theme.colors.border};
          &:hover { background: ${theme.colors.surfaceHover}; }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #f44336;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #0d1a26;
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 16px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0, 255, 136, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 40px;
  color: #00ff88;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin-top: 24px;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #00ff88, #00d4ff);
  border-radius: 3px;
  width: ${({ $progress }) => $progress}%;
  transition: width 0.1s linear;
`;

const RedirectText = styled.p`
  color: #888;
  font-size: 13px;
  margin-top: 12px;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 24px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  border-bottom: 2px solid ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  margin-bottom: -2px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const WaiverSigningPage: React.FC = () => {
  const { fieldId } = useParams<{ fieldId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = user !== null;

  const isGuestMode = searchParams.get('mode') === 'guest';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [template, setTemplate] = useState<WaiverTemplate | null>(null);

  const [activeTab, setActiveTab] = useState<'read' | 'form'>('read');
  const [hasAgreed, setHasAgreed] = useState(false);

  // Guest info
  
  // Form data
  const [formData, setFormData] = useState<WaiverFormData>({
    fullName: '',
    dateOfBirth: undefined,
    phone: '',
    email: '',
  });

  const [address, setAddress] = useState<AddressInfo>({
    street: '',
    city: '',
    state: '',
    zipCode: '',
  });

  const [emergencyContact, setEmergencyContact] = useState<EmergencyContactInfo>({
    name: '',
    phone: '',
    relationship: '',
  });

  // Signature
  const [signatureData, setSignatureData] = useState<SignatureData>({
    checkboxAgreed: false,
    graphicalSignatureData: undefined,
    typedName: '',
  });

  // Email validation error
  const [emailError, setEmailError] = useState<string | undefined>();

  useEffect(() => {
    if (fieldId) {
      loadTemplate();
    }
  }, [fieldId]);

  useEffect(() => {
    if (user && !isGuestMode) {
      setFormData((prev) => ({
        ...prev,
        fullName: user.name || '',
        email: user.email || '',
      }));
    }
  }, [user, isGuestMode]);

  // Smooth progress bar for success modal (10 seconds = 10000ms, update every 100ms)
  useEffect(() => {
    if (success) {
      const totalDuration = 10000;
      const updateInterval = 100;
      const increment = 100 / (totalDuration / updateInterval);

      const interval = setInterval(() => {
        setProgress((prev) => {
          const next = prev + increment;
          if (next >= 100) {
            clearInterval(interval);
            navigate(`/fields/${fieldId}`);
            return 100;
          }
          return next;
        });
      }, updateInterval);

      return () => clearInterval(interval);
    }
  }, [success, fieldId, navigate]);

  const loadTemplate = async () => {
    setLoading(true);
    setError(null);
    try {
      const tmpl = await waiverService.getCurrentTemplate(fieldId!);
      setTemplate(tmpl);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load waiver template');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!template) return false;

    // Check required fields
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }

    const rf = template.requiredFields;

    if (rf.dateOfBirth.enabled && rf.dateOfBirth.required && !formData.dateOfBirth) {
      setError('Date of birth is required');
      return false;
    }

    if (rf.email.enabled && rf.email.required && !formData.email?.trim()) {
      setError('Email is required');
      return false;
    }

    if (rf.email.enabled && formData.email && !isValidEmail(formData.email)) {
      setError('Invalid email format');
      return false;
    }

    if (rf.phone.enabled && rf.phone.required && !formData.phone?.trim()) {
      setError('Phone number is required');
      return false;
    }

    if (rf.emergencyContact.enabled && rf.emergencyContact.required) {
      if (!emergencyContact.name.trim() || !emergencyContact.phone.trim()) {
        setError('Emergency contact information is required');
        return false;
      }
    }

    // Check signature
    const ss = template.signatureSettings;
    if (ss.graphicalSignature && ss.requireBoth && !signatureData.graphicalSignatureData) {
      setError('Please provide your signature');
      return false;
    }
    if (ss.graphicalSignature && !ss.checkboxAgreement && !signatureData.graphicalSignatureData) {
      setError('Please provide your signature');
      return false;
    }
    if (ss.checkboxAgreement && !signatureData.checkboxAgreed) {
      setError('Please check the agreement box');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !template) return;

    setSubmitting(true);
    setError(null);

    try {
      const fullFormData: WaiverFormData = {
        ...formData,
        address: template.requiredFields.address.enabled ? address : undefined,
        emergencyContact: template.requiredFields.emergencyContact.enabled ? emergencyContact : undefined,
      };

      if (isGuestMode || !isAuthenticated) {
        await waiverService.signWaiverAsGuest({
          fieldId: fieldId!,
          guestInfo: {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
          },
          formData: fullFormData,
          signatureData,
        });
      } else {
        await waiverService.signWaiver({
          fieldId: fieldId!,
          formData: fullFormData,
          signatureData,
        });
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sign waiver');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <div style={{ textAlign: 'center', padding: '60px' }}>Loading waiver...</div>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (success) {
    const countdown = Math.ceil((100 - progress) / 10);
    return (
      <ModalOverlay>
        <ModalContent>
          <SuccessIcon>✓</SuccessIcon>
          <h2 style={{ color: '#00ff88', margin: '0 0 12px', fontSize: '24px' }}>
            Waiver Signed Successfully!
          </h2>
          <p style={{ color: '#fff', margin: 0, opacity: 0.8 }}>
            Thank you for signing the waiver. You're all set to play!
          </p>
          <ProgressBarContainer>
            <ProgressBar $progress={progress} />
          </ProgressBarContainer>
          <RedirectText>
            Redirecting to field page in {countdown} second{countdown !== 1 ? 's' : ''}...
          </RedirectText>
        </ModalContent>
      </ModalOverlay>
    );
  }

  if (!template) {
    return (
      <PageContainer>
        <ContentWrapper>
          <ErrorMessage>No active waiver template found for this field.</ErrorMessage>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Title>{template.name}</Title>
          <Subtitle>Please read and sign the waiver below</Subtitle>
        </Header>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Tabs>
          <Tab $active={activeTab === 'read'} onClick={() => setActiveTab('read')}>
            1. Read Waiver
          </Tab>
          <Tab
            $active={activeTab === 'form'}
            onClick={() => hasAgreed && setActiveTab('form')}
            style={{ opacity: hasAgreed ? 1 : 0.5, cursor: hasAgreed ? 'pointer' : 'not-allowed' }}
          >
            2. Fill Form & Sign
          </Tab>
        </Tabs>

        {activeTab === 'read' && (
          <>
            <WaiverPreview title={template.name} sections={template.sections} />
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Button $variant="primary" onClick={() => {
                setHasAgreed(true);
                setActiveTab('form');
                window.scrollTo(0, 0);
              }}>
                I Agree & Continue
              </Button>
            </div>
          </>
        )}

        {activeTab === 'form' && (
          <>
            <Card>
              <CardTitle>Personal Information</CardTitle>
              <FormGrid>
                <FormField $fullWidth>
                  <Label>
                    Full Name<RequiredMark>*</RequiredMark>
                  </Label>
                  <Input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="Enter your full legal name"
                  />
                </FormField>

                {template.requiredFields.dateOfBirth.enabled && (
                  <FormField>
                    <Label>
                      Date of Birth
                      {template.requiredFields.dateOfBirth.required && <RequiredMark>*</RequiredMark>}
                    </Label>
                    <Input
                      type="date"
                      value={formData.dateOfBirth || ''}
                      onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                  </FormField>
                )}

                {template.requiredFields.email.enabled && (
                  <FormField>
                    <Label>
                      Email
                      {template.requiredFields.email.required && <RequiredMark>*</RequiredMark>}
                    </Label>
                    <Input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (emailError) setEmailError(undefined);
                      }}
                      onBlur={() => {
                        if (formData.email && !isValidEmail(formData.email)) {
                          setEmailError(ValidationMessages.invalidEmail);
                        } else {
                          setEmailError(undefined);
                        }
                      }}
                      placeholder="your@email.com"
                    />
                    {emailError && <span style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>{emailError}</span>}
                  </FormField>
                )}

                {template.requiredFields.phone.enabled && (
                  <FormField>
                    <Label>
                      Phone
                      {template.requiredFields.phone.required && <RequiredMark>*</RequiredMark>}
                    </Label>
                    <PhoneInput
                      value={formData.phone || ''}
                      onChange={(value) => setFormData({ ...formData, phone: value })}
                    />
                  </FormField>
                )}

                {template.requiredFields.idNumber.enabled && (
                  <FormField>
                    <Label>
                      ID Number
                      {template.requiredFields.idNumber.required && <RequiredMark>*</RequiredMark>}
                    </Label>
                    <Input
                      type="text"
                      value={formData.idNumber || ''}
                      onChange={(e) => setFormData({ ...formData, idNumber: e.target.value })}
                      placeholder="Driver's License or ID"
                    />
                  </FormField>
                )}

                {template.requiredFields.idIssueDate.enabled && (
                  <FormField>
                    <Label>
                      ID Issue Date
                      {template.requiredFields.idIssueDate.required && <RequiredMark>*</RequiredMark>}
                    </Label>
                    <Input
                      type="date"
                      value={formData.idIssueDate || ''}
                      onChange={(e) => setFormData({ ...formData, idIssueDate: e.target.value })}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                  </FormField>
                )}
              </FormGrid>
            </Card>

            {template.requiredFields.address.enabled && (
              <Card>
                <CardTitle>
                  Address
                  {template.requiredFields.address.required && <RequiredMark>*</RequiredMark>}
                </CardTitle>
                <FormGrid>
                  <FormField $fullWidth>
                    <Label>Street Address</Label>
                    <Input
                      type="text"
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      placeholder="123 Main St"
                    />
                  </FormField>
                  <FormField>
                    <Label>City</Label>
                    <Input
                      type="text"
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      placeholder="City"
                    />
                  </FormField>
                  <FormField>
                    <Label>State</Label>
                    <Input
                      type="text"
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value })}
                      placeholder="State"
                    />
                  </FormField>
                  <FormField>
                    <Label>ZIP Code</Label>
                    <Input
                      type="text"
                      value={address.zipCode}
                      onChange={(e) => setAddress({ ...address, zipCode: e.target.value })}
                      placeholder="12345"
                    />
                  </FormField>
                </FormGrid>
              </Card>
            )}

            {template.requiredFields.emergencyContact.enabled && (
              <Card>
                <CardTitle>
                  Emergency Contact
                  {template.requiredFields.emergencyContact.required && <RequiredMark>*</RequiredMark>}
                </CardTitle>
                <FormGrid>
                  <FormField>
                    <Label>Contact Name</Label>
                    <Input
                      type="text"
                      value={emergencyContact.name}
                      onChange={(e) => setEmergencyContact({ ...emergencyContact, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </FormField>
                  <FormField>
                    <Label>Contact Phone</Label>
                    <PhoneInput
                      value={emergencyContact.phone}
                      onChange={(value) => setEmergencyContact({ ...emergencyContact, phone: value })}
                    />
                  </FormField>
                  <FormField>
                    <Label>Relationship</Label>
                    <Input
                      type="text"
                      value={emergencyContact.relationship}
                      onChange={(e) => setEmergencyContact({ ...emergencyContact, relationship: e.target.value })}
                      placeholder="Parent, Spouse, Friend, etc."
                    />
                  </FormField>
                </FormGrid>
              </Card>
            )}

            <Card>
              <CardTitle>Signature</CardTitle>

              {template.signatureSettings.graphicalSignature && (
                <div style={{ marginBottom: '20px' }}>
                  <Label>Draw your signature below</Label>
                  <SignaturePad
                    onSignatureChange={(data) =>
                      setSignatureData({ ...signatureData, graphicalSignatureData: data || undefined })
                    }
                  />
                </div>
              )}

              {template.signatureSettings.checkboxAgreement && (
                <CheckboxContainer>
                  <input
                    type="checkbox"
                    checked={signatureData.checkboxAgreed}
                    onChange={(e) => setSignatureData({ ...signatureData, checkboxAgreed: e.target.checked })}
                  />
                  <CheckboxText>
                    {template.signatureSettings.checkboxText || DEFAULT_CHECKBOX_TEXT}
                  </CheckboxText>
                </CheckboxContainer>
              )}
            </Card>

            <Button $variant="primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Sign Waiver'}
            </Button>
          </>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default WaiverSigningPage;
